import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { bool } from 'prop-types';

import { Page, NoticeboardFeed } from '../../components';
import { CommunitySocialRow } from '../../components/CommunityChannels/CommunityChannels';
import ShopperZuluLogo from '../../components/ShopperZuluLogo/ShopperZuluLogo';
import { getBxApp } from '../../config/bxApps';
import { PRODUCT_BRAND } from '../../config/productBrand';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import LandingAssetMarket from '../LandingPage/LandingAssetMarket';

import css from './SimpleLandingPage.module.css';

/** Six consumer apps on the tablet home — labels match product names. */
const SIMPLE_LANDING_APPS = [
  { id: 'xchange', label: 'xChange', hint: 'Swap for cash' },
  { id: 'xbuy', label: 'Buy & Sell', hint: 'Post or find items' },
  { id: 'xstays', label: 'Rentals', hint: 'Rent your item' },
  { id: 'xcourier', label: 'Services', hint: 'Service providers' },
  { id: 'xproxy', label: 'Pay & Deliver', hint: 'Payers, Prepping & Delivery' },
  { id: 'xevents', label: 'Events', hint: 'Host or attend' },
];

const resolveSimpleView = pathname => {
  if (pathname.startsWith('/landing/browse')) {
    return 'browse';
  }
  if (pathname.startsWith('/landing/run')) {
    return 'run';
  }
  if (pathname.startsWith('/landing/sell')) {
    return 'sell';
  }
  return 'home';
};

const QuietShell = ({ view, children }) => {
  const showBack = view !== 'home';
  return (
    <div className={css.root} data-testid="simple-landing-root">
      <div className={css.shell}>
        <header className={css.topBar}>
          {showBack ? (
            <Link to="/landing" className={css.backBtn} data-testid="simple-landing-back">
              ← Back
            </Link>
          ) : (
            <ShopperZuluLogo variant="mark" to="/landing" />
          )}
          {showBack ? (
            <Link to="/landing" className={css.brandMark}>
              {PRODUCT_BRAND.name}
            </Link>
          ) : (
            <Link to="/" className={css.ghostLink} data-testid="simple-landing-classic">
              Full site
            </Link>
          )}
        </header>
        <main id="main-content" className={css.main}>
          {children}
        </main>
      </div>
    </div>
  );
};

const HomeView = () => (
  <section className={css.home} data-testid="simple-landing-home">
    <div className={css.hero}>
      <div className={css.heroLogoWrap}>
        <ShopperZuluLogo variant="hero" to={null} />
      </div>
      <h1 className={css.brand}>{PRODUCT_BRAND.motto}</h1>
      <p className={css.tagline}>Pick an app. Do Digital Exchanges. Chat with the Community.</p>
    </div>

    <div className={css.actions} role="navigation" aria-label="Apps">
      {SIMPLE_LANDING_APPS.map(entry => {
        const app = getBxApp(entry.id);
        const href = app?.hubPath || `/apps/${entry.id}`;
        const accent = app?.accent || '#01bc8d';
        return (
          <Link
            key={entry.id}
            to={href}
            className={css.tile}
            style={{ '--tile-accent': accent }}
            data-testid={`simple-landing-app-${entry.id}`}
          >
            <span className={css.tileLabel}>{entry.label}</span>
            <span className={css.tileHint}>{entry.hint}</span>
          </Link>
        );
      })}
    </div>

    <p className={css.footerNote}>
      <Link to="/landing/browse">Browse listings</Link>
      {' · '}
      <Link to="/">Classic site</Link>
    </p>
    <CommunitySocialRow appId="landing" />
  </section>
);

const BrowseView = () => (
  <section className={css.panel} data-testid="simple-landing-browse-view">
    <div className={css.panelHead}>
      <h1 className={css.panelTitle}>Browse</h1>
      <p className={css.panelSub}>Tap a listing when you want to message.</p>
    </div>
    <div className={css.panelBody}>
      <LandingAssetMarket variant="browse" />
    </div>
  </section>
);

const RunView = ({ isAuthenticated }) => (
  <section className={css.panel} data-testid="simple-landing-run-view">
    <div className={css.panelHead}>
      <h1 className={css.panelTitle}>Deliver</h1>
      <p className={css.panelSub}>Open gigs nearby. Sign in only when you claim one.</p>
    </div>
    <div className={css.panelBody}>
      <NoticeboardFeed isAuthenticated={isAuthenticated} deferAuthUntilClaim />
    </div>
  </section>
);

const SellView = () => {
  const routes = useRouteConfiguration();
  const newListingPath = createResourceLocatorString('NewListingPage', routes, {}, {});

  return (
    <section className={css.panel} data-testid="simple-landing-sell-view">
      <div className={css.panelHead}>
        <h1 className={css.panelTitle}>Sell</h1>
        <p className={css.panelSub}>Ready when you are.</p>
      </div>
      <div className={css.actionStack}>
        <Link
          to={newListingPath}
          className={css.primaryAction}
          data-testid="simple-landing-post-ad"
        >
          Post an ad
        </Link>
        <Link to="/landing/browse" className={css.secondaryAction}>
          Keep browsing
        </Link>
      </div>
    </section>
  );
};

export const SimpleLandingPageComponent = props => {
  const { scrollingDisabled, isAuthenticated } = props;
  const { pathname } = useLocation();
  const view = resolveSimpleView(pathname);

  let body = <HomeView />;
  if (view === 'browse') {
    body = <BrowseView />;
  } else if (view === 'run') {
    body = <RunView isAuthenticated={isAuthenticated} />;
  } else if (view === 'sell') {
    body = <SellView />;
  }

  return (
    <Page
      title={PRODUCT_BRAND.name}
      description={`${PRODUCT_BRAND.motto} — browse, talk, settle your way.`}
      scrollingDisabled={scrollingDisabled}
    >
      <QuietShell view={view}>{body}</QuietShell>
    </Page>
  );
};

SimpleLandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  isAuthenticated: bool.isRequired,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: state.auth.isAuthenticated,
});

const SimpleLandingPage = compose(connect(mapStateToProps))(SimpleLandingPageComponent);

export default SimpleLandingPage;
